import { App as CapApp } from '@capacitor/app';
import { isNative } from '../platform/capacitor';
import {
  ensurePushRegistration,
  resolveFcmToken,
  resolveFcmTokenWithRetry,
} from '../platform/notifications';
import { isGuestNeptuneAlreadySynced, syncPushTokenIfNeeded } from './deviceToken';
import { getCachedSession, saveSession } from './user';
import { setV2BearerToken } from './client';

function normalizeMobile(raw) {
  return String(raw || '').replace(/\D/g, '').slice(-10);
}

/** @param {object|null|undefined} session */
export function resolveMobileFromSession(session, explicitMobile) {
  const explicit = normalizeMobile(explicitMobile);
  if (explicit.length === 10) return explicit;

  const top = normalizeMobile(session?.mobile);
  if (top.length === 10) return top;

  const user = session?.user;
  const fromUser = normalizeMobile(user?.mobile ?? user?.mobileNo);
  if (fromUser.length === 10) return fromUser;

  return '';
}

/**
 * Logged out and NEPTUNE already done (first open) — no more device-token calls until login.
 */
async function shouldSkipTokenApi(mobile) {
  if (mobile.length === 10) return false;
  return isGuestNeptuneAlreadySynced();
}

/**
 * Merge FCM token into session; sync only when logged in or first-open guest (NEPTUNE once).
 * @param {string} token
 * @param {string} [explicitMobile]
 * @param {{ forceUserDoc?: boolean }} [opts]
 */
export async function onFcmTokenReady(token, explicitMobile, opts = {}) {
  if (!token) return;

  const current = await getCachedSession();
  const mobile = resolveMobileFromSession(current, explicitMobile);
  const next = { ...(current || {}), pushToken: token };
  if (mobile.length === 10) next.mobile = next.mobile || mobile;

  await saveSession(next);
  if (next.token) setV2BearerToken(next.token);

  if (await shouldSkipTokenApi(mobile)) {
    console.log('[v2-push] skip token API (logged out, NEPTUNE already synced)');
    return;
  }

  await syncPushTokenIfNeeded({
    deviceToken: token,
    mobile: mobile.length === 10 ? mobile : undefined,
    forceUserDoc: Boolean(opts.forceUserDoc && mobile.length === 10),
  });
}

let ensureInFlight = null;

/**
 * First app open while logged out: subscribe FCM token to NEPTUNE once.
 */
export async function syncGuestNeptuneOnFirstOpen() {
  if (!isNative()) return;
  if (await isGuestNeptuneAlreadySynced()) return;
  return ensurePushTokenSynced();
}

/**
 * Bind token to user doc on login (and retry FCM if not ready yet).
 * @param {string} mobile
 */
export async function syncUserPushTokenAfterLogin(mobile) {
  if (!isNative()) return;

  const digits = normalizeMobile(mobile);
  if (digits.length !== 10) return;

  const session = await getCachedSession();
  let token = session?.pushToken || null;
  if (!token) {
    await ensurePushRegistration();
    token = await resolveFcmToken();
  }
  if (!token) {
    console.log('[v2-push] login: no FCM yet, ensurePushTokenSynced will retry');
    return;
  }

  await onFcmTokenReady(token, digits, { forceUserDoc: true });
}

/**
 * Resolve FCM and sync. Logged-in only, or first-open guest (no mobile).
 * @param {{ mobile?: string, forceUserDoc?: boolean }} [options]
 */
export async function ensurePushTokenSynced(options = {}) {
  if (!isNative()) return;

  const session = await getCachedSession();
  const mobile = resolveMobileFromSession(session, options.mobile);
  const explicitMobile = mobile.length === 10 ? mobile : undefined;

  if (!explicitMobile && (await isGuestNeptuneAlreadySynced())) {
    return;
  }

  if (ensureInFlight) return ensureInFlight;

  ensureInFlight = (async () => {
    const granted = await ensurePushRegistration();
    if (!granted) return;

    const deviceToken = await resolveFcmTokenWithRetry();
    if (!deviceToken) return;

    await onFcmTokenReady(deviceToken, explicitMobile, {
      forceUserDoc: options.forceUserDoc,
    });
  })().finally(() => {
    ensureInFlight = null;
  });

  return ensureInFlight;
}

/**
 * Foreground: sync only when logged in (no token API after logout).
 * @returns {Promise<() => void>}
 */
export async function setupForegroundPushSync() {
  if (!isNative()) return () => {};

  const handle = await CapApp.addListener('appStateChange', async ({ isActive }) => {
    if (!isActive) return;

    const session = await getCachedSession();
    const mobile = resolveMobileFromSession(session);
    if (mobile.length !== 10) return;

    ensurePushTokenSynced({ mobile });
  });

  return () => {
    handle.remove();
  };
}
